import { useState, useEffect } from 'react';
import { Badge } from '@etherisc/ui-kit';
import { InfoIcon } from 'lucide-react';

interface VersionInfo {
  semantic: string;
  full: string;
  buildTime: string;
  gitCommitShort: string;
  environment: string;
}

interface VersionDisplayProps {
  className?: string;
  showBuildInfo?: boolean;
  variant?: 'minimal' | 'detailed';
}

export function VersionDisplay({ 
  className = '', 
  showBuildInfo = false, 
  variant = 'minimal' 
}: VersionDisplayProps) {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        // Try to get version from build-time environment variables first
        const buildTimeVersion = {
          semantic: import.meta.env.VITE_VERSION || '0.1.0',
          full: import.meta.env.VITE_VERSION_FULL || `${import.meta.env.VITE_VERSION || '0.1.0'}+${import.meta.env.VITE_GIT_COMMIT_SHORT || 'dev'}`,
          buildTime: import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
          gitCommitShort: import.meta.env.VITE_GIT_COMMIT_SHORT || 'dev',
          environment: import.meta.env.VITE_ENVIRONMENT || 'development'
        };

        // If we have build-time info, use it
        if (import.meta.env.VITE_VERSION) {
          setVersionInfo(buildTimeVersion);
          setLoading(false);
          return;
        }

        // Otherwise, fetch from API
        const response = await fetch('/health');
        if (response.ok) {
          const data = await response.json();
          setVersionInfo({
            semantic: data.version || '0.1.0',
            full: data.fullVersion || data.version || '0.1.0',
            buildTime: data.buildTime || new Date().toISOString(),
            gitCommitShort: data.gitCommit || 'unknown',
            environment: data.environment || 'development'
          });
        } else {
          // Fallback to build-time info even if incomplete
          setVersionInfo(buildTimeVersion);
        }
      } catch (error) {
        console.warn('Failed to fetch version info:', error);
        // Use fallback version info
        setVersionInfo({
          semantic: '0.1.0',
          full: '0.1.0+unknown',
          buildTime: new Date().toISOString(),
          gitCommitShort: 'unknown',
          environment: 'development'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchVersionInfo();
  }, []);

  if (loading || !versionInfo) {
    return (
      <div className={`text-xs text-gray-400 ${className}`}>
        Loading version...
      </div>
    );
  }

  const getEnvironmentVariant = (env: string): 'success' | 'warning' | 'destructive' | 'secondary' => {
    switch (env.toLowerCase()) {
      case 'production':
        return 'destructive';
      case 'staging':
        return 'warning';
      case 'testing':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-2 text-xs text-gray-500 ${className}`}>
        <span>v{versionInfo.full}</span>
        <span>
          Built: {new Date(versionInfo.buildTime).toLocaleDateString()}
        </span>
        <Badge variant={getEnvironmentVariant(versionInfo.environment)} className="text-xs px-1 py-0">
          {versionInfo.environment}
        </Badge>
      </div>
    );
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center gap-2 text-xs">
        <InfoIcon className="w-3 h-3 text-gray-400" />
        <span className="text-gray-600 font-medium">Version:</span>
        <span className="font-mono text-gray-800">{versionInfo.full}</span>
        <Badge variant={getEnvironmentVariant(versionInfo.environment)} className="text-xs">
          {versionInfo.environment}
        </Badge>
      </div>
      
      {showBuildInfo && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>
            Built: {new Date(versionInfo.buildTime).toLocaleDateString()}
          </span>
          <span className="font-mono">
            {versionInfo.gitCommitShort}
          </span>
        </div>
      )}
    </div>
  );
}
